import { LoadingButton } from "@mui/lab"
import { Alert, AlertTitle, Button, Grid, Typography } from "@mui/material"
import moment from "moment"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import NricNumberField from "../../../components/Draft/NricNumberField"
import DateOfBirthInputField from "../../../components/PersonalAndContactDetails/FormFields/DateOfBirthInputField"
import { MembershipDraftRequest, postMembershipDraft, postMembershipDraftMembership } from "../../../services/ntuc/memberService/draft"
import DateOfBirtValidator from "../../../services/validation/personal-and-contact-details-form/DateOfBirthValidator"
import NricValidator from "../../../services/validation/personal-and-contact-details-form/NricValidator"
import { ActionType } from "../../../store/signup/SignupReducer"
import { useSignupStore } from "../../../store/signup/UserSignupStore"
import { SignupRoute, SignupRouteParams } from "../SignupFlow/SignupFlow"

interface DraftFormState {
    nric: {
        value: string,
        error: boolean,
        helperText: string,
    },
    dob: {
        value: Date | null,
        error: boolean,
        helperText: string,
    },
    disabled: boolean,
    loading: boolean,
}

function DraftPage(params: SignupRouteParams) {
    const { onNavigateTo } = params
    const [, dispatch] = useSignupStore()
    const { t } = useTranslation(`draft`)
    const [error, setError] = useState({
        errorCode: ``,
        errorDescription: ``,
    })

    const MAX = 4
    const [form, setForm] = useState<DraftFormState>({
        nric: {
            value: ``,
            error: false,
            helperText: ``,
        },
        dob: {
            value: null,
            error: false,
            helperText: ``,
        },
        disabled: true,
        loading: false,
    })

    const startLoading = () => {
        setForm((form) => {
            return {
                ...form,
                loading: true,
            }
        })
    }

    const stopLoading = () => {
        setForm((form) => {
            return {
                ...form,
                loading: false,
            }
        })
    }

    const fetchDraft = (payload: MembershipDraftRequest) => {
        if (window.myNtucConfig.authToken) {
            return postMembershipDraftMembership(payload)
        } else {
            return postMembershipDraft(payload)
        }
    }

    const handleRetrieveDraft = () => {
        startLoading()
        fetchDraft({
            nric: form.nric.value.slice(-MAX),
            dob: moment(form.dob.value).format(`YYYY-MM-DD`),
        })
            .then(response => response.data.content)
            .then(dispatchDraft)
            .then(redirectToSignupPage)
            .catch(() => {
                setError({
                    errorCode: t(`error.code`),
                    errorDescription: t(`error.description`),
                })
            })
            .finally(stopLoading)
    }

    const dispatchDraft = (content: object) => {
        dispatch({
            type: ActionType.RETRIEVE_DRAFT,
            value: content,
        })
    }

    const redirectToSignupPage = () => {
        onNavigateTo(SignupRoute.Form)
    }

    const handleReturnClick = () => {
        onNavigateTo(SignupRoute.Introduction)
    }

    const handleNricChange = (value: string) => {
        const validationText = (new NricValidator()).validate(value)

        setForm({
            ...form,
            nric: {
                helperText: validationText ?? ``,
                error: validationText !== undefined,
                value: value,
            },
            disabled: value === ``
                || form.dob.value === null
                || form.dob.error
                || validationText !== undefined,
        })
    }

    const handleDobChange = (date: Date | null) => {
        const validationText = (new DateOfBirtValidator()).validate(date)

        setForm({
            ...form,
            dob: {
                helperText: validationText ?? ``,
                error: validationText !== undefined,
                value: date,
            },
            disabled: date === null
                || form.nric.value === ``
                || form.nric.error
                || validationText !== undefined,
        })
    }

    return (
        <Grid container xs={12} direction="column" rowSpacing={2} justifyContent="center" padding={2}>
            <Grid item xs={8} md={8}>
                <Typography>{t(`info`)}</Typography>
            </Grid>
            <Grid item xs={12} md={8}>
                {
                    error.errorCode && <Alert severity="error">
                        <AlertTitle>{error.errorCode}</AlertTitle>
                        <Button onClick={() => onNavigateTo(SignupRoute.Introduction)} fullWidth>{error.errorDescription}</Button>
                    </Alert>
                }
            </Grid>
            <Grid item xs={12} md={8}>
                <NricNumberField
                    value={form.nric.value}
                    error={form.nric.error}
                    helperText={form.nric.helperText}
                    onChange={handleNricChange}
                />
            </Grid>
            <Grid item xs={12} md={8}>
                <DateOfBirthInputField
                    defaultValue={form.dob.value}
                    errorText={form.dob.helperText}
                    isDisabled={false}
                    handleChange={handleDobChange}
                />
            </Grid>
            <Grid item xs={12} sm={8} textAlign="center">
                <LoadingButton loading={form.loading} disabled={form.disabled} fullWidth onClick={handleRetrieveDraft} color="primary" variant="contained">{t(`retrieve`)}</LoadingButton>
            </Grid>
            <Grid item xs={12} sm={8} textAlign="center">
                <Button fullWidth onClick={handleReturnClick} color="primary" variant="outlined">{t(`return`)}</Button>
            </Grid>
        </Grid>
    )
}

export default DraftPage
